<template>
  <Layout>
    <section class="pricing-faq-page">

      <!-- TITLE -->
      <div class="container py-5 text-center">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <h1 class="font-weight-bold text-primary mb-4">
              {{ $t('pricing.title') }}
            </h1>
            <p class="lead text-muted">
              {{ $t('pricing.subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <!-- PRICING -->
      <div class="container pb-5">
        <div class="row">

          <!-- FREE -->
          <div class="col-md-6 col-lg-3 mb-4">
            <div class="card h-100 pricing-card">
              <div class="card-body d-flex flex-column">
                <h6 class="text-uppercase font-weight-bold text-primary">
                  {{ $t('pricing.plans.free.title') }}
                </h6>

                <h2 class="font-weight-bold mt-3">
                  {{ $t('pricing.plans.free.price') }}
                  <small class="text-muted">/ {{ $t('pricing.month') }}</small>
                </h2>

                <p class="text-muted small">
                  {{ $t('pricing.plans.free.note') }}
                </p>

                <ul class="list-unstyled mt-4 mb-4 flex-grow-1">
                  <li
                    v-for="(f, i) in $t('pricing.plans.free.features')"
                    :key="i"
                    class="d-flex align-items-start mb-3"
                  >
                    <img alt="check" class="mr-2" width="20" src="@/assets/icons/check-circle-s.svg"/>

                    <span>{{ f }}</span>
                  </li>
                </ul>

                <button class="btn btn-light font-weight-bold mt-auto">
                  {{ $t('pricing.cta.free') }}
                </button>
              </div>
            </div>
          </div>

          <!-- STANDARD -->
          <div class="col-md-6 col-lg-3 mb-4">
            <div class="card h-100 pricing-card">
              <div class="card-body d-flex flex-column">
                <h6 class="text-uppercase font-weight-bold text-primary">
                  {{ $t('pricing.plans.standard.title') }}
                </h6>

                <h2 class="font-weight-bold mt-3">
                  {{ $t('pricing.plans.standard.price') }}
                  <small class="text-muted">/ {{ $t('pricing.month') }}</small>
                </h2>

                <p class="text-muted small">
                  {{ $t('pricing.plans.standard.note') }}
                </p>

                <ul class="list-unstyled mt-4 mb-4 flex-grow-1">
                  <li
                    v-for="(f, i) in $t('pricing.plans.standard.features')"
                    :key="i"
                    class="d-flex align-items-start mb-3"
                  >
                    <img alt="check" class="mr-2" width="20" src="@/assets/icons/check-circle-s.svg"/>
                    <span>{{ f }}</span>
                  </li>
                </ul>

                <button class="btn btn-secondary text-white font-weight-bold mt-auto">
                  {{ $t('pricing.cta.standard') }}
                </button>
              </div>
            </div>
          </div>

          <!-- GOLD -->
          <div class="col-md-6 col-lg-3 mb-4">
            <div class="card h-100 pricing-card gold-card position-relative">

              <span class="badge badge-warning popular-badge">
                {{ $t('pricing.badgePopular') }}
              </span>

              <div class="card-body d-flex flex-column">
                <h6 class="text-uppercase font-weight-bold text-primary">
                  {{ $t('pricing.plans.gold.title') }}
                </h6>

                <h2 class="font-weight-bold mt-3">
                  {{ $t('pricing.plans.gold.price') }}
                  <small class="text-muted">/ {{ $t('pricing.month') }}</small>
                </h2>

                <p class="text-warning font-weight-bold small">
                  {{ $t('pricing.plans.gold.note') }}
                </p>

                <ul class="list-unstyled mt-4 mb-4 flex-grow-1">
                  <li
                    v-for="(f, i) in $t('pricing.plans.gold.features')"
                    :key="i"
                    class="d-flex align-items-start mb-3"
                  >
                    <img alt="check" width="20" class="mr-2" src="@/assets/icons/star-s.svg"/>
                    <span class="font-weight-medium">{{ f }}</span>
                  </li>
                </ul>

                <button class="btn btn-secondary btn-lg text-white font-weight-bold mt-auto">
                  {{ $t('pricing.cta.gold') }}
                </button>
              </div>
            </div>
          </div>

          <!-- PREMIUM -->
          <div class="col-md-6 col-lg-3 mb-4">
            <div class="card h-100 pricing-card">
              <div class="card-body d-flex flex-column">
                <h6 class="text-uppercase font-weight-bold text-primary">
                  {{ $t('pricing.plans.premium.title') }}
                </h6>

                <h2 class="font-weight-bold mt-3">
                  {{ $t('pricing.plans.premium.price') }}
                  <small class="text-muted">/ {{ $t('pricing.month') }}</small>
                </h2>

                <p class="text-muted small">
                  {{ $t('pricing.plans.premium.note') }}
                </p>

                <ul class="list-unstyled mt-4 mb-4 flex-grow-1">
                  <li
                    v-for="(f, i) in $t('pricing.plans.premium.features')"
                    :key="i"
                    class="d-flex align-items-start mb-3"
                  >
                    <img alt="check" class="mr-2" width="20" src="@/assets/icons/check-circle-s.svg"/>
                    <span>{{ f }}</span>
                  </li>
                </ul>

                <button class="btn btn-dark font-weight-bold mt-auto">
                  {{ $t('pricing.cta.premium') }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- FAQ -->
      <div class="bg-light py-5">
        <div class="container">
          <h2 class="text-center text-primary font-weight-bold mb-5">
            {{ $t('faq.title') }}
          </h2>

          <div class="row justify-content-center">
            <div class="col-lg-8">

              <div
                v-for="(faq, i) in $t('faq.items')"
                :key="i"
                class="card mb-3 faq-card"
              >
                <div class="card-header bg-white">
                  <button
                    class="btn btn-link btn-block text-left font-weight-bold d-flex justify-content-between"
                    @click="toggleFaq(i)"
                  >
                    {{ faq.q }}


                    <img
                      v-if="openFaq === i"
                      alt="minus"
                      width="25"
                      src="@/assets/icons/minus-s.svg"
                    />

                    <img
                      v-else
                      alt="add"
                      width="25"
                      src="@/assets/icons/add-s.svg"
                    />

                  </button>
                </div>

                <div v-show="openFaq === i" class="card-body text-muted">
                  {{ faq.a }}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    </section>
  </Layout>
</template>


<script>
import Layout from "../../layouts/main";

export default {
  name: "PricingFaq",
  components: { Layout },

  data() {
    return {
      openFaq: null
    };
  },

  methods: {
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index;
    }
  }
};
</script>



<style>
.pricing-card {
  margin: 10px;
  border-radius: 16px;
  transition: all 0.25s ease;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(0,0,0,.08);
}

.gold-card {
  border: 2px solid #D4AF37;
  transform: scale(1.05);
}

@media (max-width: 991px) {
  .gold-card {
    transform: scale(1);
  }
}

.popular-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 14px;
  font-size: 11px;
  text-transform: uppercase;
}

.faq-card {
  border-radius: 14px;
}

</style>